import {
	EmailChangeEligibilityReason,
	EmailChangeEligibilityView,
} from '@dealstack/shared/lib/common/emailChangeEligibility';
import { isValidEmail } from '@dealstack/shared/lib/validation';
import { API_FETCH_ABORT_SYMBOL } from '@dealstack/frontend/shared/other/react/ApiWrapper/symbols';
import { ApiWrapperError } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';

export const ELIGIBILITY_UNAVAILABLE_EXPLANATION =
	'Could not check whether this email can be changed. The change is blocked until that check succeeds - try again shortly.';

const isTransientFailure = (error: unknown): boolean => {
	if (!(error instanceof ApiWrapperError)) {
		return true;
	}
	if (error.symbol === API_FETCH_ABORT_SYMBOL) {
		return false;
	}
	return error.status === undefined || error.status >= 500;
};

export const shouldRetryEligibility = (failureCount: number, error: unknown): boolean =>
	failureCount < 1 && isTransientFailure(error);

export const toEligibilityTarget = (
	typedEmail: string | undefined,
	currentEmail: string | undefined
): string | undefined => {
	const candidate = typedEmail?.trim().toLowerCase() ?? '';

	if (!isValidEmail(candidate)) {
		return undefined;
	}

	return candidate === currentEmail?.trim().toLowerCase() ? undefined : candidate;
};

export const describeEligibility = (
	eligibility: EmailChangeEligibilityView | undefined
): string | null => {
	if (!eligibility || eligibility.allowed) {
		return null;
	}

	const organisation = eligibility.organizationName ?? 'their organisation';

	switch (eligibility.reason) {
		case EmailChangeEligibilityReason.DOMAIN_MANAGED_BY_IDP:
			return `This email is managed by ${organisation} through their identity provider. You can still change it here: the organisation membership is reassigned automatically, but if the user stays active in the identity provider it may reassert the old address on their next SSO login.`;
		case EmailChangeEligibilityReason.TARGET_EMAIL_IN_USE:
			return 'That address is already in use by another account.';
		case EmailChangeEligibilityReason.VERIFICATION_UNAVAILABLE:
			return 'Could not verify domain ownership with the identity provider. Try again.';
		default:
			return 'This email address cannot be changed from here.';
	}
};
